import React from 'react';
import {requestPaymentDiscount} from './payment-discount-request';

const empty='data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
let current=null,pending=null;
let lastRefresh=0;
const listeners=new Set();
const preloadedPaymentImages=new Set();
function preloadPaymentImages(images){
  if(typeof Image==='undefined')return;
  ['AlipayLogo5.png','AlipayLogo12.png','visa.png'].forEach(name=>{
    const url=images[name];
    if(typeof url!=='string'||!/^https:\/\/cms\.opcms\.org\/discount-assets\/[a-f0-9]{64}\.png$/.test(url))return;
    if(preloadedPaymentImages.has(url))return;
    preloadedPaymentImages.add(url);
    const image=new Image();image.decoding='async';
    image.onerror=()=>preloadedPaymentImages.delete(url);
    image.src=url;
  });
}
const rates={"100":20.54,"95":19.51,"90":18.49,"85":17.46,"80":16.44,"75":15.41,"70":14.38,"65":13.42};
const keys=['currency-btn-cny.png','AlipayLogo5.png','AlipayLogo12.png'];
export function validateDiscount(value){
  if(!value||value.schemaVersion!==1||!Object.prototype.hasOwnProperty.call(rates,value.mode)||value.rate!==rates[value.mode]||!Number.isSafeInteger(value.revision)||value.revision<1)throw new Error('Invalid discount configuration');
  if(value.showAntpay3!==undefined&&typeof value.showAntpay3!=='boolean')throw new Error('Invalid payment button visibility');
  if(value.imageVariant!=='standard')throw new Error('Invalid image variant');
  if(!value.images||keys.some(k=>typeof value.images[k]!=='string'||!/^https:\/\/cms\.opcms\.org\/discount-assets\/[a-f0-9]{64}\.png$/.test(value.images[k])))throw new Error('Invalid discount images');
  return value;
}
export async function refreshDiscount(fetcher=fetch){
  if(pending)return pending.promise;
  const job={cancelled:false};
  const interrupted=new Promise((resolve,reject)=>{
    job.cancel=()=>{
      job.cancelled=true;
      if(pending===job)pending=null;
      const error=new Error('Discount refresh paused');error.cancelled=true;reject(error);
      // Detach this artwork subscriber without aborting the countdown's shared request.
    };
  });
  pending=job;
  job.promise=(async()=>{
    try{
      const value=await Promise.race([Promise.resolve().then(async()=>{
        if(job.cancelled)return;
        // Coalesce a burst of initial mounts; ongoing price revalidation stays enabled.
        const response=await requestPaymentDiscount({fetcher,maxAgeMs:1000});
        return validateDiscount(response.value);
      }),interrupted]);
      if(job.cancelled)return;
      current=value;lastRefresh=Date.now();preloadPaymentImages(value.images);listeners.forEach(fn=>fn(value));return value;
    }finally{if(pending===job)pending=null;}
  })();return job.promise;
}
// Speculative warming may reuse a recent result; mounted price screens still refresh.
export function warmDiscount(){return current&&Date.now()-lastRefresh<15000?Promise.resolve(current):refreshDiscount();}
// The existing validated CMS snapshot can render artwork without waiting for another provider.
export function peekDiscount(){return current;}
// Reuse the cash buttons' validated artwork notification; no new request/timer.
export function subscribeDiscount(listener){
  listeners.add(listener);
  return ()=>listeners.delete(listener);
}
// Missing settings keep the existing visible default; only antpay3 is controlled.
export function paymentButtonVisible(provider){return provider!=='antpay3'||!current||current.showAntpay3!==false;}
export function discountImage(name){return current&&current.images[name]||empty;}
// A small wrapper refreshes image URLs; prices continue to come from the payment APIs.
export function withDiscountImages(View){
  return class DiscountImages extends React.Component {
    constructor(props){super(props);this.state={revision:0,unavailable:false,priceChanged:false};this.alive=false;this.rate=null;this.generation=0;this.suspended=false;this.request=null;this.changed=value=>{
      if(!this.alive||this.suspended)return;
      const changed=this.rate!==null&&this.rate!==value.rate;
      if(this.rate===null)this.rate=value.rate;
      this.setState(state=>({revision:value.revision,unavailable:false,priceChanged:state.priceChanged||changed}));
    };
    this.hidden=()=>typeof document!=='undefined'&&document.visibilityState==='hidden';
    this.pause=()=>{
      this.suspended=true;this.generation++;this.request=null;clearTimeout(this.timer);
      // iOS can suspend a pending fetch while the notice page is in front.
      if(pending)pending.cancel();
    };
    this.resume=()=>{
      if(this.hidden())return;
      if(this.suspended&&['CNY','RMB'].includes(this.props.currency))this.setState({unavailable:true});
      this.suspended=false;return this.refresh();
    };
    this.visibility=()=>this.hidden()?this.pause():this.resume();
    this.refresh=()=>{
      if(!this.alive||this.suspended||this.hidden())return Promise.resolve();
      if(this.request)return this.request;
      clearTimeout(this.timer);const generation=this.generation;
      const active=()=>this.alive&&!this.suspended&&!this.hidden()&&generation===this.generation;
      this.request=refreshDiscount().catch(error=>{
        if(active()&&!error.cancelled)this.setState({unavailable:true});
      }).finally(()=>{
        if(generation!==this.generation)return;
        this.request=null;
        if(active())this.timer=setTimeout(this.refresh,15000);
      });
      return this.request;
    };}
    componentDidMount(){
      this.alive=true;listeners.add(this.changed);if(current)this.changed(current);
      window.addEventListener('focus',this.resume);window.addEventListener('pageshow',this.resume);
      window.addEventListener('pagehide',this.pause);window.addEventListener('online',this.resume);
      document.addEventListener('visibilitychange',this.visibility);this.visibility();
    }
    componentWillUnmount(){
      this.alive=false;this.generation++;listeners.delete(this.changed);clearTimeout(this.timer);
      window.removeEventListener('focus',this.resume);window.removeEventListener('pageshow',this.resume);
      window.removeEventListener('pagehide',this.pause);window.removeEventListener('online',this.resume);
      document.removeEventListener('visibilitychange',this.visibility);
    }
    render(){
      const cny=['CNY','RMB'].includes(this.props.currency);
      const products=this.props.product?[this.props.product]:(this.props.products||[]);
      const mismatch=this.props.ready&&products.some(p=>!p.cmsPricing||!current||p.cmsPricing.rate!==current.rate||p.cmsPricing.mode!==current.mode);
      if(cny&&(this.state.priceChanged||mismatch||this.state.unavailable))return React.createElement('div',{role:'status',style:{padding:20,textAlign:'center'}},'優惠已更新或暫時未能確認，請重新載入儲值頁面取得最新金額。',React.createElement('button',{type:'button',onClick:()=>window.location.reload()},'重新載入'));
      // An interrupted configuration refresh does not mean cached artwork failed.
      const missingArtwork=this.state.unavailable&&!current;
      return React.createElement(React.Fragment,null,missingArtwork?React.createElement('p',{role:'status'},'優惠圖片暫時無法載入。',React.createElement('button',{type:'button',onClick:this.refresh},'重試')):null,React.createElement(View,this.props));
    }
  };
}
